'use client';

import { useState, useMemo, useCallback, useRef, useEffect, type ReactNode } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { SERVICES } from '@/lib/constants';
import { formatResponseTime } from '@/lib/formatResponseTime';
import type { ServiceType } from '@/types/database';
import { CustomDropdown } from '@/components/shared/custom-dropdown';
import { EndorsementUI } from '@/components/country/endorsement-ui';
import { RankingsAttribution } from '@/components/country/rankings-attribution';
import { CountryFlag } from '@/components/ui/country-flag';
import { useCurrentUser } from '@/hooks/use-current-user';
import { normalizeWebsiteForHref } from '@/lib/safe-external-url';
import posthog from 'posthog-js';

const YELLOW_PAGES_PER_PAGE = 20;

/** Generate an array of page numbers with ellipses for compact pagination display */
function generatePageNumbers(currentPage: number, totalPages: number): (number | '...')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [1];

  if (currentPage > 3) {
    pages.push('...');
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 2) {
    pages.push('...');
  }

  pages.push(totalPages);

  return pages;
}

interface Endorser {
  id: string;
  company_name: string;
  logo_url: string | null;
}

interface RankedForwarder {
  id: string;
  company_name: string;
  city: string | null;
  slug: string | null;
  headline: string | null;
  services: ServiceType[];
  networks: string[];
  certifications: string[];
  is_founding_member: boolean;
  logo_url: string | null;
  website: string | null;
  avg_response_time_hours: number | null;
  test_rfqs_sent: number;
  benchmark_rfqs_evaluated: number;
  benchmark_last_evaluated_at: string | null;
  ranking_score: number | null;
  quote_completeness_score: number | null;
  rank_position: number;
  score: number;
  serviceRankings: Array<{
    service_type: ServiceType;
    rank_position: number;
  }>;
  endorsementCount: number;
  endorsers: Endorser[];
}

interface PaidForwarder {
  id: string;
  company_name: string;
  city: string | null;
  slug: string | null;
  headline: string | null;
  about: string | null;
  services: ServiceType[];
  networks: string[];
  certifications: string[];
  logo_url: string | null;
  website: string | null;
  endorsementCount: number;
  endorsers: Endorser[];
}

interface YellowPagesListing {
  id: string;
  company_name: string;
  city: string | null;
  website: string | null;
  networks: string[];
  services?: ServiceType[];
  slug?: string | null;
  source: 'profile' | 'import';
}

interface OtherCountry {
  slug: string;
  name: string;
  code: string;
}

interface CountryClientProps {
  countryName: string;
  countryCode: string;
  countrySlug: string;
  rankedForwarders: RankedForwarder[];
  paidForwarders: PaidForwarder[];
  yellowPagesListings: YellowPagesListing[];
  allServices: string[];
  allCities: string[];
  otherCountries?: OtherCountry[];
  /** Service listicle pages that exist for this country (≥3 ranked entries). */
  servicePages?: { slug: string; label: string }[];
}

export function CountryClient({
  countryName,
  countryCode,
  countrySlug,
  rankedForwarders,
  paidForwarders,
  yellowPagesListings,
  allServices,
  allCities,
  otherCountries: otherCountriesProp,
  servicePages = [],
}: CountryClientProps) {
  // Resolved client-side so the page itself stays statically renderable — see
  // the note in use-current-user.ts.
  const { userId: currentUserId, isForwarder: isCurrentUserForwarder } = useCurrentUser();

  const [searchQuery, setSearchQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'ranking' | 'response_time' | 'score'>('ranking');
  const [yellowPagesSearch, setYellowPagesSearch] = useState('');
  const [yellowPagesPage, setYellowPagesPage] = useState(1);
  const yellowPagesSectionRef = useRef<HTMLElement>(null);

  // Apply the ?service= deep-link (used by /directory and the landing hero)
  // after hydration. Read from the URL rather than a server prop: touching
  // searchParams on the server would force a dynamic render on every request
  // and would keep the full listing out of the cached HTML.
  useEffect(() => {
    const service = new URLSearchParams(window.location.search).get('service');
    if (service) setServiceFilter(service);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    posthog.capture('country_page_viewed', {
      country: countryName,
      country_code: countryCode,
      service_filter: serviceFilter !== 'all' ? serviceFilter : undefined,
      ranked_count: rankedForwarders.length,
      featured_count: paidForwarders.length,
      directory_count: yellowPagesListings.length,
    });
  }, []);

  // Get service label
  const getServiceLabel = (serviceSlug: string) => {
    const service = SERVICES.find((s) => s.id === serviceSlug);
    return service?.name || serviceSlug;
  };

  // Filter and sort ranked forwarders
  // NOTE: Ranked forwarders are ALWAYS shown regardless of service filter.
  // Only text search, city filter, and sort apply to ranked forwarders.
  const filteredRankedForwarders = useMemo(() => {
    let result = rankedForwarders;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.company_name.toLowerCase().includes(query) ||
          (f.city && f.city.toLowerCase().includes(query)) ||
          (f.headline && f.headline.toLowerCase().includes(query))
      );
    }

    // City filter
    if (cityFilter !== 'all') {
      result = result.filter((f) => f.city === cityFilter);
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'response_time':
          const aTime = a.avg_response_time_hours ?? Infinity;
          const bTime = b.avg_response_time_hours ?? Infinity;
          return aTime - bTime;
        case 'score':
          return (b.score || 0) - (a.score || 0);
        case 'ranking':
        default:
          return a.rank_position - b.rank_position;
      }
    });

    return result;
  }, [rankedForwarders, searchQuery, cityFilter, sortBy]);

  // Filter featured forwarders
  const filteredPaidForwarders = useMemo(() => {
    let result = paidForwarders;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.company_name.toLowerCase().includes(query) ||
          (f.city && f.city.toLowerCase().includes(query)) ||
          (f.headline && f.headline.toLowerCase().includes(query))
      );
    }

    if (serviceFilter !== 'all') {
      result = result.filter((f) => f.services?.includes(serviceFilter as ServiceType));
    }

    if (cityFilter !== 'all') {
      result = result.filter((f) => f.city === cityFilter);
    }

    return result;
  }, [paidForwarders, searchQuery, serviceFilter, cityFilter]);

  // Filter yellow pages listings
  const filteredYellowPages = useMemo(() => {
    let result = yellowPagesListings;

    // Service filter
    if (serviceFilter !== 'all') {
      result = result.filter((f) => f.services?.includes(serviceFilter as ServiceType));
    }

    // Search filter
    if (yellowPagesSearch) {
      const query = yellowPagesSearch.toLowerCase();
      result = result.filter(
        (f) =>
          f.company_name.toLowerCase().includes(query) ||
          (f.city && f.city.toLowerCase().includes(query)) ||
          f.networks.some((n) => n.toLowerCase().includes(query))
      );
    }

    return result;
  }, [yellowPagesListings, serviceFilter, yellowPagesSearch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setYellowPagesPage(1);
  }, [yellowPagesSearch, serviceFilter]);

  // Pagination derived values
  const yellowPagesTotalPages = Math.ceil(filteredYellowPages.length / YELLOW_PAGES_PER_PAGE);
  const yellowPagesStart = (yellowPagesPage - 1) * YELLOW_PAGES_PER_PAGE;
  const yellowPagesEnd = yellowPagesStart + YELLOW_PAGES_PER_PAGE;
  const paginatedYellowPages = filteredYellowPages.slice(yellowPagesStart, yellowPagesEnd);

  const handleYellowPagesPageChange = useCallback((page: number) => {
    setYellowPagesPage(page);
    // Scroll to yellow pages section
    yellowPagesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Other countries for explore section
  const otherCountries = (otherCountriesProp || [
    { slug: 'united-states', name: 'United States', code: 'US' },
    { slug: 'germany', name: 'Germany', code: 'DE' },
    { slug: 'netherlands', name: 'Netherlands', code: 'NL' },
    { slug: 'china', name: 'China', code: 'CN' },
    { slug: 'latvia', name: 'Latvia', code: 'LV' },
    { slug: 'united-kingdom', name: 'United Kingdom', code: 'GB' },
  ]).filter((c) => c.slug !== countrySlug);

  return (
    <>
      {/* Filters Bar */}
      <div className="filters-bar country-filters">
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search forwarders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">Service Type</label>
          <CustomDropdown
            value={serviceFilter}
            onChange={setServiceFilter}
            options={[
              { value: 'all', label: 'All Services' },
              ...allServices.map((service) => ({
                value: service,
                label: getServiceLabel(service),
              })),
            ]}
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">City</label>
          <CustomDropdown
            value={cityFilter}
            onChange={setCityFilter}
            options={[
              { value: 'all', label: 'All Cities' },
              ...allCities.map((city) => ({
                value: city,
                label: city,
              })),
            ]}
          />
        </div>
        <div className="filter-group filter-group-last">
          <label className="filter-label">Sort By</label>
          <CustomDropdown
            value={sortBy}
            onChange={(value) => setSortBy(value as 'ranking' | 'response_time' | 'score')}
            options={[
              { value: 'ranking', label: 'Ranking' },
              { value: 'response_time', label: 'Response Time' },
              { value: 'score', label: 'Overall Score' },
            ]}
          />
        </div>
      </div>

      {/* Main Content */}
      <main>
        {/* Section: Ranked Forwarders */}
        {filteredRankedForwarders.length > 0 && (
          <section className="directory-section">
            <div className="section-header">
              <h2 className="section-title">Top Ranked in {countryName}</h2>
              <span className="section-count">{filteredRankedForwarders.length} forwarders</span>
            </div>

            <RankingsAttribution />

            {servicePages.length > 0 && (
              <p style={{ fontSize: '0.85rem', margin: '0 0 1rem' }}>
                Best by service:{' '}
                {servicePages.map((s, i) => (
                  <span key={s.slug}>
                    {i > 0 && ' · '}
                    <Link
                      href={`/${countrySlug}/services/${s.slug}`}
                      style={{ textDecoration: 'underline' }}
                    >
                      {s.label}
                    </Link>
                  </span>
                ))}
              </p>
            )}

            {/* Directory Header */}
            <div className="directory-columns-header">
              <div className="col-header col-header-rank">Rank</div>
              <div className="col-header">Operator</div>
              <div className="col-header">Performance</div>
              <div className="col-header">Services</div>
              <div className="col-header col-header-action">Action</div>
            </div>

            {/* Directory Items */}
            {/*
              Each row is a <div> shell, not one big <Link>: the profile link is
              stretched over the card via CSS so the whole row stays clickable,
              which leaves room for a real <a href> to the forwarder's website.
              Crawlers only credit anchors, so window.open / nested links here
              would mean shipzy.ai never shows up in the forwarder's Search Console.
            */}
            {filteredRankedForwarders.map((forwarder) => {
              const safeWebsite = normalizeWebsiteForHref(forwarder.website);
              return (
              <div
                key={forwarder.id}
                className="directory-item country-directory-item listing-card-shell"
              >
                <div className="cell cell-rank">
                  <span className="rank-badge">#{forwarder.rank_position}</span>
                </div>
                <div className="cell cell-operator">
                  <div className="operator-info">
                    {forwarder.logo_url ? (
                      <Image
                        src={forwarder.logo_url}
                        alt={forwarder.company_name}
                        width={48}
                        height={48}
                        className="operator-logo"
                      />
                    ) : (
                      <div className="operator-logo-placeholder">
                        {forwarder.company_name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="operator-text">
                      <div className="company-name">
                        <Link
                          href={forwarder.slug ? `/forwarders/${forwarder.slug}` : '#'}
                          className="listing-stretched-link"
                          onClick={() => posthog.capture('forwarder_card_clicked', {
                            forwarder_name: forwarder.company_name,
                            forwarder_id: forwarder.id,
                            rank_position: forwarder.rank_position,
                            type: 'ranked',
                            country: countryName,
                          })}
                        >
                          {forwarder.company_name}
                        </Link>
                      </div>
                      <div className="company-meta">
                        {forwarder.city && `${forwarder.city} • `}
                        {forwarder.certifications?.slice(0, 2).join(', ')}
                        {safeWebsite && (
                          <>
                            {(forwarder.city || forwarder.certifications?.length) ? ' • ' : ''}
                            <a
                              href={safeWebsite}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="listing-website-link"
                              onClick={() => posthog.capture('forwarder_website_clicked', {
                                company_name: forwarder.company_name,
                                listing_id: forwarder.id,
                                type: 'ranked',
                                country: countryName,
                              })}
                            >
                              Website ↗
                            </a>
                          </>
                        )}
                      </div>
                      {/* Endorsements - Interactive UI with avatars and endorse button */}
                      {((forwarder.endorsers && forwarder.endorsers.length > 0) || isCurrentUserForwarder) && (
                        <div style={{ marginTop: '8px' }} className="listing-interactive">
                          <EndorsementUI
                            forwarderId={forwarder.id}
                            currentUserId={currentUserId}
                            isCurrentUserForwarder={isCurrentUserForwarder}
                            initialEndorsements={forwarder.endorsers || []}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="cell cell-performance">
                  {forwarder.avg_response_time_hours !== null &&
                   forwarder.avg_response_time_hours !== undefined &&
                   Number.isFinite(forwarder.avg_response_time_hours) && (
                    <div className="performance-metric">
                      <span className="metric-value">
                        {formatResponseTime(forwarder.avg_response_time_hours, { format: 'short' })}
                      </span>
                      <span className="metric-label">Avg Response</span>
                    </div>
                  )}
                  {forwarder.score && (
                    <div className="performance-metric">
                      <span className="metric-value">{forwarder.score}/100</span>
                      <span className="metric-label">Score</span>
                    </div>
                  )}
                  {forwarder.quote_completeness_score !== null &&
                   forwarder.quote_completeness_score !== undefined && (
                    <div className="performance-metric">
                      <span className="metric-value">{forwarder.quote_completeness_score}/100</span>
                      <span className="metric-label">Quote Quality</span>
                    </div>
                  )}
                  {forwarder.benchmark_rfqs_evaluated > 0 && (
                    <div className="performance-metric">
                      <span className="metric-value">{forwarder.benchmark_rfqs_evaluated}</span>
                      <span className="metric-label">Benchmark RFQs</span>
                    </div>
                  )}
                </div>
                <div className="cell cell-services">
                  <div className="tags-container">
                    {forwarder.services?.slice(0, 3).map((service) => {
                      const serviceRanking = forwarder.serviceRankings.find(
                        (sr) => sr.service_type === service
                      );
                      return (
                        <span
                          key={service}
                          className={`tag ${serviceRanking?.rank_position === 1 ? 'tag-highlight' : ''}`}
                        >
                          {getServiceLabel(service)}
                          {serviceRanking?.rank_position === 1 && ' #1'}
                        </span>
                      );
                    })}
                    {forwarder.services && forwarder.services.length > 3 && (
                      <span className="tag tag-more">+{forwarder.services.length - 3}</span>
                    )}
                  </div>
                </div>
                <div className="cell cell-action">
                  <svg
                    className="arrow-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      strokeLinecap="square"
                    />
                  </svg>
                </div>
              </div>
              );
            })}

            <p style={{ fontSize: '0.85rem', marginTop: '1rem' }}>
              Rankings are earned through measured performance, never bought. Operating in{' '}
              {countryName}?{' '}
              <Link href="/signup" style={{ textDecoration: 'underline', fontWeight: 600 }}>
                Apply to get ranked
              </Link>{' '}
              and compete for the #1 position.
            </p>
          </section>
        )}

        {/* No rankings published yet — honest framing plus a recruiting hook:
            the top of the page must not imply the directory below is a "best"
            list, and the empty ranking slot is the pitch to forwarders. */}
        {rankedForwarders.length === 0 && (
          <section className="cta-section">
            <div className="cta-content">
              <h3 className="cta-title">No performance rankings in {countryName} yet</h3>
              <p className="cta-text">
                Shipzy ranks forwarders by measured RFQ response time, quote quality, and peer
                endorsements — not paid placement. Be among the first ranked in {countryName} and
                take the #1 position.
              </p>
              <Link href="/signup" className="cta-button">
                Apply to Get Ranked
                <svg className="arrow-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" strokeLinecap="square" />
                </svg>
              </Link>
            </div>
          </section>
        )}

        {/* Section: Featured Forwarders */}
        {filteredPaidForwarders.length > 0 && (
          <section className="directory-section featured-section">
            <div className="section-header">
              <h2 className="section-title">Featured Forwarders & Verified Profiles</h2>
              <span className="section-count">{filteredPaidForwarders.length} forwarders</span>
            </div>
            <p className="section-description">
              Verified logistics providers not yet in performance rankings
            </p>

            {/* Featured Grid */}
            <div className="featured-grid">
              {filteredPaidForwarders.map((forwarder) => {
                const safeWebsite = normalizeWebsiteForHref(forwarder.website);
                return (
                <div key={forwarder.id} className="featured-card listing-card-shell">
                  <div className="featured-card-header">
                    {forwarder.logo_url ? (
                      <Image
                        src={forwarder.logo_url}
                        alt={forwarder.company_name}
                        width={56}
                        height={56}
                        className="featured-logo"
                      />
                    ) : (
                      <div className="featured-logo-placeholder">
                        {forwarder.company_name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="featured-info">
                      <h3 className="featured-name">
                        <Link
                          href={forwarder.slug ? `/forwarders/${forwarder.slug}` : '#'}
                          className="listing-stretched-link"
                          onClick={() => posthog.capture('forwarder_card_clicked', {
                            forwarder_name: forwarder.company_name,
                            forwarder_id: forwarder.id,
                            type: 'featured',
                            country: countryName,
                          })}
                        >
                          {forwarder.company_name}
                        </Link>
                      </h3>
                      <p className="featured-location">
                        {forwarder.city || countryName}
                        {safeWebsite && (
                          <>
                            {' • '}
                            <a
                              href={safeWebsite}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="listing-website-link"
                              onClick={() => posthog.capture('forwarder_website_clicked', {
                                company_name: forwarder.company_name,
                                listing_id: forwarder.id,
                                type: 'featured',
                                country: countryName,
                              })}
                            >
                              Website ↗
                            </a>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  {forwarder.headline && (
                    <p className="featured-headline">{forwarder.headline}</p>
                  )}
                  {/* Endorsements - Interactive UI with avatars and endorse button */}
                  {((forwarder.endorsers && forwarder.endorsers.length > 0) || isCurrentUserForwarder) && (
                    <div style={{ marginTop: '12px' }} className="listing-interactive">
                      <EndorsementUI
                        forwarderId={forwarder.id}
                        currentUserId={currentUserId}
                        isCurrentUserForwarder={isCurrentUserForwarder}
                        initialEndorsements={forwarder.endorsers || []}
                      />
                    </div>
                  )}
                  <div className="featured-services">
                    {forwarder.services?.slice(0, 4).map((service) => (
                      <span key={service} className="tag">
                        {getServiceLabel(service)}
                      </span>
                    ))}
                    {forwarder.services && forwarder.services.length > 4 && (
                      <span className="tag tag-more">+{forwarder.services.length - 4}</span>
                    )}
                  </div>
                  <div className="featured-cta">
                    View Profile
                    <svg className="arrow-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" strokeLinecap="square" />
                    </svg>
                  </div>
                </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Section: Yellow Pages Directory */}
        {yellowPagesListings.length > 0 && (
          <section className="directory-section yellow-pages-section" ref={yellowPagesSectionRef}>
            <div className="section-header">
              <h2 className="section-title">Freight Forwarders Directory</h2>
              <span className="section-subtitle">(Yellow Pages)</span>
            </div>
            <p className="section-description">
              Unranked listings. Claim your profile to verify details and get discovered.
            </p>

            {/*
              Crawlable entry point into the paginated directory. The grid below
              pages via useState with no URL, so a crawler only ever sees the
              first 20 cards — this link is what gives the remaining profiles a
              reachable path. Keep it server-rendered and keep it an <a>.
            */}
            <p className="section-description yp-browse-all">
              <Link href={`/${countrySlug}/directory/1`}>
                Browse all {yellowPagesListings.length} companies in {countryName} →
              </Link>
            </p>

            {/* Yellow Pages Search */}
            <div className="yellow-pages-search">
              <input
                type="text"
                className="filter-input"
                placeholder="Search directory by company, city, or network..."
                value={yellowPagesSearch}
                onChange={(e) => setYellowPagesSearch(e.target.value)}
              />
            </div>

            {/* Yellow Pages Grid */}
            {filteredYellowPages.length > 0 ? (
              <>
                <div className="yellow-pages-grid">
                  {paginatedYellowPages.map((listing) => {
                    const safeWebsite = normalizeWebsiteForHref(listing.website);
                    const websiteLink = safeWebsite && (
                      <a
                        href={safeWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="yp-link listing-website-link"
                        onClick={() => posthog.capture('forwarder_website_clicked', {
                          company_name: listing.company_name,
                          listing_id: listing.id,
                          type: 'yellow_pages',
                          country: countryName,
                        })}
                      >
                        Website ↗
                      </a>
                    );
                    const renderCardBody = (name: ReactNode) => (
                      <>
                        <div className="yp-card-header">
                          <div className="yp-initials">
                            {listing.company_name.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="yp-info">
                            <h4 className="yp-name">{name}</h4>
                            {listing.city && <p className="yp-location">{listing.city}</p>}
                          </div>
                        </div>
                        {listing.networks.length > 0 && (
                          <div className="yp-networks">
                            {listing.networks.slice(0, 3).map((network) => (
                              <span key={network} className="yp-network-tag">{network}</span>
                            ))}
                          </div>
                        )}
                      </>
                    );
                    
                    return listing.slug ? (
                      <div
                        key={listing.id}
                        className="yellow-pages-card yellow-pages-card-link listing-card-shell"
                      >
                        {renderCardBody(
                          <Link href={`/forwarders/${listing.slug}`} className="listing-stretched-link">
                            {listing.company_name}
                          </Link>
                        )}
                        <div className="yp-actions">
                          {websiteLink}
                        </div>
                      </div>
                    ) : (
                      <div 
                        key={listing.id}
                        className="yellow-pages-card"
                      >
                        {renderCardBody(listing.company_name)}
                        <div className="yp-actions">
                          {websiteLink}
                          <Link href="/signup" className="yp-claim">
                            Claim Profile
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                <div className="yp-pagination">
                  <div className="yp-pagination-info">
                    Showing {yellowPagesStart + 1}–{Math.min(yellowPagesEnd, filteredYellowPages.length)} of {filteredYellowPages.length} listings
                  </div>
                  {yellowPagesTotalPages > 1 && (
                    <div className="yp-pagination-controls">
                      <button
                        className="yp-page-btn yp-page-prev"
                        onClick={() => handleYellowPagesPageChange(yellowPagesPage - 1)}
                        disabled={yellowPagesPage <= 1}
                        aria-label="Previous page"
                      >
                        ← Prev
                      </button>
                      <div className="yp-page-numbers">
                        {generatePageNumbers(yellowPagesPage, yellowPagesTotalPages).map((page, i) =>
                          page === '...' ? (
                            <span key={`ellipsis-${i}`} className="yp-page-ellipsis">…</span>
                          ) : (
                            <button
                              key={page}
                              className={`yp-page-btn yp-page-num ${yellowPagesPage === page ? 'yp-page-active' : ''}`}
                              onClick={() => handleYellowPagesPageChange(page as number)}
                            >
                              {page}
                            </button>
                          )
                        )}
                      </div>
                      <button
                        className="yp-page-btn yp-page-next"
                        onClick={() => handleYellowPagesPageChange(yellowPagesPage + 1)}
                        disabled={yellowPagesPage >= yellowPagesTotalPages}
                        aria-label="Next page"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <p>No listings match your search criteria.</p>
              </div>
            )}
          </section>
        )}

        {/* CTA Section — skipped when the no-rankings CTA already ran at the top */}
        {rankedForwarders.length > 0 && (
          <section className="cta-section">
            <div className="cta-content">
              <h3 className="cta-title">Operating in {countryName}?</h3>
              <p className="cta-text">
                Join the ranking and get discovered by shippers looking for logistics partners.
              </p>
              <Link href="/signup" className="cta-button">
                Apply for Ranking
                <svg className="arrow-icon" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" strokeLinecap="square" />
                </svg>
              </Link>
            </div>
          </section>
        )}

        {/* Other Countries Section */}
        <section className="regions-section">
          <h3 className="section-title">Explore Other Regions</h3>
          <div className="regions-grid">
            {otherCountries.slice(0, 5).map((c) => (
              <Link key={c.slug} href={`/${c.slug}`} className="region-card">
                <CountryFlag code={c.code} size={24} className="region-flag" />
                <span className="region-name">{c.name}</span>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}
